import { useState, useCallback, useRef, useEffect } from 'react';
import { MenuProvider } from './context/MenuContext';
import { CartProvider, useCart } from './context/CartContext';
import { MenuView } from './components/MenuView';
import { CartDrawer } from './components/CartDrawer';
import { CheckoutForm } from './components/CheckoutForm';
import { OrderConfirmation } from './components/OrderConfirmation';
import { OrderWaiting } from './components/OrderWaiting';
import { InfoPage } from './components/InfoPage';
import { ReservationForm } from './components/ReservationForm';
import { useMenu } from './context/MenuContext';
import { I18nContext, createTranslator, useI18n, LANG_LABELS, LANG_FLAGS, ALL_LANGS, type Lang } from './i18n';

/** Language requested by the host page, e.g. the restaurant site passes
 *  ?lang=en on its English pages. Wins over the visitor's stored choice. */
function getUrlLang(): Lang | null {
  const l = new URLSearchParams(window.location.search).get('lang');
  return l && (ALL_LANGS as string[]).includes(l) ? (l as Lang) : null;
}

function getMode(): 'reservation' | 'order' {
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'reservation') return 'reservation';
  return 'order';
}

function WidgetContent() {
  const { loading, error, business, onlineStatus, widgetTheme, minDeliveryTime, showMinDeliveryTime } = useMenu();
  const { view } = useCart();
  const { t } = useI18n();
  const mode = getMode();

  if (loading) {
    return <div className="hc-loading">{t('menu.loading')}</div>;
  }

  if (error) {
    return <div className="hc-error">{t('menu.error')} {error}</div>;
  }

  // Reservation mode — separate standalone view
  if (mode === 'reservation') {
    return (
      <div className={`hc-widget hc-theme-${widgetTheme}`}>
        <WidgetHeader />
        <main className="hc-main">
          <ReservationForm />
        </main>
      </div>
    );
  }

  const unavailable = !!(onlineStatus && (!onlineStatus.is_open || onlineStatus.is_paused));
  const bannerKey = onlineStatus?.is_paused ? 'banner.paused' : 'banner.closed';

  return (
    <div className={`hc-widget hc-theme-${widgetTheme}`}>
      <WidgetHeader />
      {unavailable && (
        <div className="hc-availability-banner" role="status" aria-live="polite">
          {t(bannerKey)}
        </div>
      )}
      {showMinDeliveryTime && minDeliveryTime != null && minDeliveryTime > 0 && view === 'menu' && !unavailable && (
        <div className="hc-delivery-notice">
          &#9202; {t('delivery.minTime', { minutes: String(minDeliveryTime) })}
        </div>
      )}
      <main className="hc-main">
        {view === 'menu' && <MenuView />}
        {view === 'cart' && <CartDrawer />}
        {view === 'checkout' && <CheckoutForm />}
        {view === 'waiting' && <OrderWaiting />}
        {view === 'confirmation' && <OrderConfirmation />}
        {view === 'info' && <InfoPage />}
      </main>
      {view === 'menu' && <CartFloatingButton />}
    </div>
  );
}

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <circle cx="12" cy="8" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.5L21 8H6" />
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="17" cy="20" r="1.4" />
  </svg>
);

function WidgetHeader() {
  const { business } = useMenu();
  const { view, setView, totalItems } = useCart();
  const { t } = useI18n();

  return (
    <header className="hc-header">
      <div className="hc-business">
        <span className="hc-business-eyebrow">{t('header.eyebrow')}</span>
        <h1 className="hc-business-name">{business?.name ?? 'HalloChef'}</h1>
      </div>
      <div className="hc-header-actions">
        <button
          className={`hc-header-btn hc-header-icon-btn ${view === 'info' ? 'hc-active' : ''}`}
          onClick={() => setView(view === 'info' ? 'menu' : 'info')}
          title={t('nav.info')}
          aria-label={t('nav.info')}
        >
          <InfoIcon />
        </button>
        <LanguageSwitcher />
        {view !== 'info' && totalItems > 0 && (
          <button
            className="hc-cart-icon-btn"
            onClick={() => setView('cart')}
            title={t('nav.cart')}
            aria-label={t('nav.cart')}
          >
            <CartIcon />
            <span>{totalItems}</span>
          </button>
        )}
      </div>
    </header>
  );
}

function LanguageSwitcher() {
  const { lang, setLang } = useI18n();
  const { widgetLanguages } = useMenu();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Offer only languages this business enabled AND that the widget can render
  // UI for. A single enabled language hides the switcher entirely.
  const offered = ALL_LANGS.filter((l) => widgetLanguages.includes(l));
  const langs = offered.length ? offered : ALL_LANGS;
  if (langs.length < 2) return null;

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="hc-lang-picker" ref={ref}>
      <button className="hc-header-btn hc-lang-btn" onClick={() => setOpen(!open)}>
        <span className="hc-lang-flag">{LANG_FLAGS[lang]}</span>
        <span>{LANG_LABELS[lang]}</span>
      </button>
      {open && (
        <div className="hc-lang-dropdown">
          {langs.map((l) => (
            <button
              key={l}
              className={`hc-lang-option ${l === lang ? 'hc-lang-active' : ''}`}
              onClick={() => { setLang(l); setOpen(false); }}
            >
              <span className="hc-lang-flag">{LANG_FLAGS[l]}</span>
              <span>{LANG_LABELS[l]}</span>
              {l === lang && <span className="hc-lang-check">&#10003;</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CartFloatingButton() {
  const { totalItems, totalPrice, setView } = useCart();
  const { t } = useI18n();
  if (totalItems === 0) return null;

  return (
    <button className="hc-cart-fab" onClick={() => setView('cart')}>
      <span className="hc-cart-fab-icon"><CartIcon /></span>
      <span className="hc-cart-fab-count">{totalItems}</span>
      <span className="hc-cart-fab-label">{t('cart.view')}</span>
      <span className="hc-cart-fab-total">{totalPrice.toFixed(2)} &euro;</span>
    </button>
  );
}

export default function App() {
  const [lang, setLang] = useState<Lang>(() => {
    const fromUrl = getUrlLang();
    if (fromUrl) return fromUrl;
    const stored = localStorage.getItem('hc_lang');
    return (ALL_LANGS as string[]).includes(stored || '') ? (stored as Lang) : 'hr';
  });
  useEffect(() => { localStorage.setItem('hc_lang', lang); }, [lang]);
  const t = useCallback(createTranslator(lang), [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      <MenuProvider>
        <CartProvider>
          <WidgetContent />
        </CartProvider>
      </MenuProvider>
    </I18nContext.Provider>
  );
}
